import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  redirectionService,
  RedirectType,
  RedirectMatchType,
  Redirection,
} from "@/services/redirectionService";
import { useDebounce } from "@/hooks/useDebounce";
import { showToast } from "@/lib/toast";
import ModalCreateRedirection from "@/components/redirection/ModalCreateRedirection";
import ModalEditRedirection from "@/components/redirection/ModalEditRedirection";
import ModalDeleteRedirection from "@/components/redirection/ModalDeleteRedirection";

export default function RedirectionPage() {
  const [items, setItems] = useState<Redirection[]>([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [isLoading, setIsLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState<Redirection | null>(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchData = async (page = pageIndex) => {
    setIsLoading(true);
    try {
      const res: any = await redirectionService.getPaging({
        pageIndex: page,
        pageSize,
        search: debouncedSearch || undefined,
      });
      const data = res?.data?.data || res?.data || res;
      setItems(data?.items || []);
      setTotalPages(data?.totalPages || 0);
    } catch (e: any) {
      showToast.error(e?.message || "Tải danh sách thất bại");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
    setPageIndex(1);
  }, [debouncedSearch]);
  useEffect(() => {
    fetchData(pageIndex);
  }, [pageIndex]);

  const typeLabel = (t: RedirectType) => t;
  const matchTypeLabel = (m: RedirectMatchType) => {
    const map: any = {
      exact: "Khớp chính xác",
      contains: "Chứa",
      starts_with: "Bắt đầu với",
      ends_with: "Kết thúc với",
      regex: "Regex",
    };
    return map[m] || m;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
              Quản lý chuyển hướng
            </h1>
            <div className="mt-2 flex items-center gap-2">
              <p className="text-gray-500 dark:text-gray-400">
                Quản lý các quy tắc chuyển hướng URL trong hệ thống
              </p>
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                {items.length} quy tắc
              </span>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Danh sách chuyển hướng</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <div className="relative w-full max-w-sm">
                <Input
                  placeholder="Tìm theo source/destination..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <Button
                onClick={() => setOpenCreate(true)}
                className="cursor-pointer"
              >
                Thêm chuyển hướng
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50/80 dark:bg-gray-800/80 backdrop-blur supports-[backdrop-filter]:bg-gray-50/60 dark:supports-[backdrop-filter]:bg-gray-800/60">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Match
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Destination
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Trạng thái
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Hành động
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                  {isLoading ? (
                    <tr>
                      <td
                        className="px-6 py-16 text-center text-gray-500 dark:text-gray-400"
                        colSpan={6}
                      >
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          <span className="ml-3">Đang tải...</span>
                        </div>
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td
                        className="px-6 py-16 text-center text-gray-500 dark:text-gray-400"
                        colSpan={6}
                      >
                        <div className="flex flex-col items-center justify-center text-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            className="h-12 w-12 text-gray-300 mb-3"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.5"
                              d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
                            />
                          </svg>
                          <p className="text-gray-600 font-medium">
                            Chưa có chuyển hướng
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            Hãy thêm chuyển hướng đầu tiên để bắt đầu
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    items.map((it) => (
                      <tr
                        key={it._id}
                        className="transition-all hover:bg-gray-50/60 dark:hover:bg-gray-800/60"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white truncate max-w-[280px]">
                          {it.source}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          {matchTypeLabel(it.matchType)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 truncate max-w-[300px]">
                          {it.destination}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {typeLabel(it.type)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {it.active ? (
                            <Badge>Đang bật</Badge>
                          ) : (
                            <Badge variant="secondary">Đang tắt</Badge>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="cursor-pointer"
                              onClick={async () => {
                                await redirectionService.toggleActive(
                                  it._id,
                                  !it.active
                                );
                                fetchData(pageIndex);
                              }}
                            >
                              Toggle
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="cursor-pointer"
                              onClick={() => {
                                setEditing(it);
                                setOpenEdit(true);
                              }}
                            >
                              Sửa
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="cursor-pointer text-red-600 hover:text-red-700 hover:border-red-300"
                              onClick={() => {
                                setDeletingId(it._id);
                              }}
                            >
                              Xóa
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-end gap-2 mt-4 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => setPageIndex((p) => Math.max(1, p - 1))}
                  disabled={pageIndex === 1}
                >
                  Trước
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .slice(
                    Math.max(0, pageIndex - 3),
                    Math.min(totalPages, pageIndex + 2)
                  )
                  .map((p) => (
                    <Button
                      key={p}
                      variant={p === pageIndex ? "default" : "outline"}
                      onClick={() => setPageIndex(p)}
                    >
                      {p}
                    </Button>
                  ))}
                <Button
                  variant="outline"
                  onClick={() =>
                    setPageIndex((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={pageIndex === totalPages}
                >
                  Sau
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <ModalCreateRedirection
          isOpen={openCreate}
          onClose={() => setOpenCreate(false)}
          onCompleted={() => fetchData(pageIndex)}
        />
        <ModalEditRedirection
          isOpen={openEdit}
          onClose={() => setOpenEdit(false)}
          data={editing}
          onCompleted={() => fetchData(pageIndex)}
        />
        <ModalDeleteRedirection
          isOpen={!!deletingId}
          onClose={() => setDeletingId(null)}
          id={deletingId}
          onCompleted={() => fetchData(pageIndex)}
        />
      </div>
    </DashboardLayout>
  );
}
